import React from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  Button,
  TextField,
  TextArea,
  Select,
  useToast,
} from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import { useCreateProductItem } from '../hooks/useProductItems';
import { STAGE_LABELS, ACTIVE_STAGES, PRIORITY_OPTIONS } from '../constants';
import type { ProductItemPriority, ProductItemStage } from '../types';

const STAGE_OPTIONS = ACTIVE_STAGES.map((s) => ({ id: s, label: STAGE_LABELS[s] }));

interface Props {
  includeDeprecated: boolean;
}

export function CreateProductItemDialog({ includeDeprecated }: Props) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [stage, setStage] = React.useState<ProductItemStage>('ideas');
  const [priority, setPriority] = React.useState<ProductItemPriority | 'none'>('none');

  const createItem = useCreateProductItem(includeDeprecated);
  const { toast } = useToast();

  const reset = () => {
    setTitle('');
    setDescription('');
    setStage('ideas');
    setPriority('none');
  };

  const canSubmit = title.trim().length > 0 && !createItem.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createItem.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        stage,
        priority: priority === 'none' ? null : priority,
      },
      {
        onSuccess: () => {
          toast.success('Product item created');
          reset();
          setIsOpen(false);
        },
        onError: () => {
          toast.error('Failed to create item');
        },
      },
    );
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="md">
      <Button variant="primary" size="sm" iconLeft={<Plus weight="bold" size={16} />}>
        New Item
      </Button>
      <Dialog title="Create Product Item" description="Add a feature idea to the product board.">
        <DialogContent>
          <TextField
            label="Title"
            value={title}
            onChange={setTitle}
            placeholder="Short feature name"
          />
          <TextArea
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="What is this feature? Who benefits?"
            className="min-h-[100px]"
          />
          <Select
            label="Stage"
            items={STAGE_OPTIONS}
            selectedKey={stage}
            onSelectionChange={(key) => setStage(key as ProductItemStage)}
          />
          <Select
            label="Priority"
            items={PRIORITY_OPTIONS}
            selectedKey={priority}
            onSelectionChange={(key) => setPriority(key as ProductItemPriority | 'none')}
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleSubmit} isDisabled={!canSubmit}>
            {createItem.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
